/**
 * @license
 * Copyright (c) %FIRST_YEAR% %CURRENT_YEAR%, Oracle and/or its affiliates.
 * Licensed under The Universal Permissive License (UPL), Version 1.0
 * as shown at https://oss.oracle.com/licenses/upl/
 * @ignore
 */

import { Dimension } from '../../utils/UNSAFE_visTypes/common';
import { Scale } from '../../utils/UNSAFE_visTypes/chart';
export function getAxisBoundsByDelta(
  xViewportExtent: { viewportMin: number; viewportMax: number },
  xAxisExtent: { min: number; max: number },
  yViewportExtent: { viewportMin: number; viewportMax: number },
  yAxisExtent: { min: number; max: number },
  xScale: Scale,
  yScale: Scale,
  xMin: number,
  xMax: number,
  yMin: number,
  yMax: number,
  isRtl: boolean,
  isHoriz: boolean
) {
  const { viewportMin: xViewportMin, viewportMax: xViewportMax } = xViewportExtent;
  const { min: xAxisMin, max: xAxisMax } = xAxisExtent;
  const { viewportMin: yViewportMin, viewportMax: yViewportMax } = yViewportExtent;
  const { min: yAxisMin, max: yAxisMax } = yAxisExtent;
  const deltas = convertToAxisCoord(xMin, xMax, yMin, yMax, isRtl, isHoriz);
  const xExtents = getAxisMinMaxByDelta(
    xViewportMin,
    xViewportMax,
    xAxisMin,
    xAxisMax,
    xScale,
    deltas['xMin'],
    deltas['xMax']
  );
  const yExtents = getAxisMinMaxByDelta(
    yViewportMin,
    yViewportMax,
    yAxisMin,
    yAxisMax,
    yScale,
    deltas['yMin'],
    deltas['yMax']
  );

  return { xExtents, yExtents };
}

export function convertToAxisCoord(
  xMin: number,
  xMax: number,
  yMin: number,
  yMax: number,
  isRtl: boolean,
  isHoriz: boolean
) {
  const axisCoord: Record<string, any> = {};
  if (isHoriz) {
    axisCoord['xMin'] = yMin;
    axisCoord['xMax'] = yMax;
    axisCoord['yMin'] = isRtl ? xMax : xMin;
    axisCoord['yMax'] = isRtl ? xMin : xMax;
  } else {
    axisCoord['xMin'] = isRtl ? xMax : xMin;
    axisCoord['xMax'] = isRtl ? xMin : xMax;
    axisCoord['yMin'] = yMax;
    axisCoord['yMax'] = yMin;
  }
  return axisCoord;
}

export function getAxisMinMaxByDelta(
  viewportMin: number,
  viewportMax: number,
  globalMin: number,
  globalMax: number,
  axisScale: Scale,
  minDelta: number,
  maxDelta: number
) {
  const minDeltaVal = axisScale.invert(minDelta) - axisScale.invert(0);
  const maxDeltaVal = axisScale.invert(maxDelta) - axisScale.invert(0);

  let weight = 1;
  const newExtent = viewportMax + maxDeltaVal - (viewportMin + minDeltaVal);
  const minExtent = 1; //axisInfo.getMinExtent();
  if (minDelta != maxDelta && newExtent < minExtent)
    weight = (viewportMax - viewportMin - minExtent) / (minDeltaVal - maxDeltaVal);

  viewportMin += minDeltaVal * weight;
  viewportMax += maxDeltaVal * weight;

  // Limit to global min/max
  if (viewportMax - viewportMin >= globalMax - globalMin) {
    viewportMin = globalMin;
    viewportMax = globalMax;
  } else if (viewportMin < globalMin) {
    viewportMax += globalMin - viewportMin;
    viewportMin = globalMin;
  } else if (viewportMax > globalMax) {
    viewportMin -= viewportMax - globalMax;
    viewportMax = globalMax;
  }

  return { viewportMin, viewportMax };
}

export function getAxisMinMaxDrag(
  offsetX: number,
  offsetY: number,
  plotAreaSpace: Dimension,
  xViewportExtent: { viewportMin: number; viewportMax: number },
  xAxisExtent: { min: number; max: number },
  isHoriz: boolean,
  isRtl: boolean
) {
  const deltaX = isRtl ? plotAreaSpace.width - offsetX : offsetX - plotAreaSpace.x;
  const deltaY = offsetY - plotAreaSpace.y;
  const deltaV = xViewportExtent.viewportMax - xViewportExtent.viewportMin;
  const delta =
    (isHoriz ? deltaY : deltaX) *
    ((xAxisExtent.max - xAxisExtent.min) / (isHoriz ? plotAreaSpace.height : plotAreaSpace.width));
  let xMin = delta - deltaV / 2;
  let xMax = delta + deltaV / 2;

  if (xMin < xAxisExtent.min) {
    xMin = xAxisExtent.min;
    xMax = xMin + deltaV;
  }

  if (xMax > xAxisExtent.max) {
    xMin = xAxisExtent.max - deltaV;
    xMax = xAxisExtent.max;
  }

  return {
    xMin,
    xMax
  };
}
